/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { isImageAsset, isVideoAsset } from "../../../utils";
import { Icon, isPlayableVideoUrl, VideoPlayer } from "@adapptio/lib-editor-ui";
import { AssetEdit } from "../../AssetEdit";
import { useResourceInfo } from "../../../../../ui/hooks";
import { RESOURCE_TYPES } from "@adapptio/lib-core";

import "./AssetEditMainPreview.scss";

export interface IAssetEditMainPreviewProps {
	className?: string;
	view: AssetEdit;
}

export const AssetEditMainPreview: React.FC<IAssetEditMainPreviewProps> = ({ className, view }) => {
	const baseClass = BEM("edt-asset-edit-main-preview");

	const resourceInfo = useResourceInfo(view);
	const assetBaseUrl = useMemo(() => view.getAssetBaseUrl(), []);

	const assetResource = resourceInfo.resource;

	if (className) {
		baseClass.add(className);
	}

	if (!assetResource?.metaData || assetResource.resourceType !== RESOURCE_TYPES.ASSET) {
		return null;
	}

	const revision = resourceInfo?.resource?.metaData?.revision;

	const { assetUrl } = assetResource.metaData;
	const assetSrcUrl = new URL(assetUrl, assetBaseUrl);
	assetSrcUrl.searchParams.set("revision", revision);
	const assetSrc = assetSrcUrl?.href;

	if (assetSrc && isImageAsset(assetResource)) {
		baseClass.mod("image");
		return <img className={baseClass.toClassName()} src={assetSrc} />;
	}

	if (assetSrc && isVideoAsset(assetResource) && isPlayableVideoUrl(assetSrc)) {
		baseClass.mod("video");
		return (
			<div className={baseClass.toClassName()}>
				<VideoPlayer url={assetSrc} controls />
			</div>
		);
	}

	const icon = isImageAsset(assetResource)
		? "mdi/file-image"
		: isVideoAsset(assetResource)
		? "mdi/file-video"
		: "mdi/file-outline";

	return (
		<div className={baseClass.toClassName()}>
			<Icon name={icon} className={baseClass.element("icon").toClassName()} />
			<div className={baseClass.element("name").toClassName()}>{assetResource.descriptor?.name}</div>
		</div>
	);
};
